import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  ShoppingCart, 
  Trash2, 
  Plus, 
  Minus,
  ArrowLeft,
  Package,
  MapPin,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomerCart = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('nas_customer_cart') || '[]');
    setCart(savedCart);
    
    const customerData = localStorage.getItem('nas_customer');
    if (customerData) {
      const parsed = JSON.parse(customerData);
      setCustomer(parsed);
      setDeliveryAddress(parsed.address || '');
    }
  }, []);

  const updateCart = (productId, quantity) => {
    let newCart = [...cart];
    const existingIndex = newCart.findIndex(c => c.product_id === productId);
    
    if (quantity <= 0) {
      newCart = newCart.filter(c => c.product_id !== productId);
    } else if (existingIndex >= 0) {
      if (quantity > newCart[existingIndex].available_quantity) {
        toast.error(`Maximum available: ${newCart[existingIndex].available_quantity}`);
        return;
      }
      newCart[existingIndex].quantity = quantity;
    }
    
    setCart(newCart);
    localStorage.setItem('nas_customer_cart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('nas_cart_updated'));
  };

  const removeItem = (productId) => {
    const newCart = cart.filter(c => c.product_id !== productId);
    setCart(newCart);
    localStorage.setItem('nas_customer_cart', JSON.stringify(newCart));
    window.dispatchEvent(new Event('nas_cart_updated'));
    toast.success('Item removed from cart');
  };

  const clearCart = () => {
    if (!window.confirm('Clear all items from cart?')) return;
    setCart([]);
    localStorage.setItem('nas_customer_cart', '[]');
    window.dispatchEvent(new Event('nas_cart_updated'));
    toast.success('Cart cleared');
  };

  const totalAmount = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleSubmitOrder = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    
    if (!deliveryAddress.trim()) {
      toast.error('Please enter delivery address');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('nas_customer_token');
      if (!token) {
        navigate('/nas-portal/login');
        return;
      }
      
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity
        })),
        notes: notes,
        payment_method: paymentMethod,
        delivery_address: deliveryAddress
      };
      
      const response = await axios.post(
        `${API}/api/nas-customer/orders?token=${token}`,
        orderData
      );
      
      // Clear cart
      localStorage.setItem('nas_customer_cart', '[]');
      window.dispatchEvent(new Event('nas_cart_updated'));
      
      setOrderSuccess(response.data);
      toast.success('Order placed successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to place order';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Order success view
  if (orderSuccess) {
    return (
      <div className="p-4 pb-20 lg:pb-4">
        <Card className="max-w-md mx-auto bg-white">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Order Placed!</h2>
            <p className="text-slate-500 mb-4">Your order has been submitted for review.</p>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-500">Delivery Code</p>
              <p className="text-2xl font-bold text-green-600">{orderSuccess.delivery_code}</p>
              <p className="text-lg font-semibold mt-2">₱{orderSuccess.total_amount?.toLocaleString()}</p>
            </div>
            
            <p className="text-sm text-slate-500 mb-6">
              Save your delivery code. You can track your order status in "My Orders".
            </p>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/nas-portal/catalog')}
              >
                Continue Shopping
              </Button>
              <Button 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => navigate('/nas-portal/orders')}
                data-testid="view-orders-btn"
              >
                View Orders
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 pb-20 lg:pb-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/nas-portal/catalog')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-xl font-bold text-slate-800">Shopping Cart</h1>
          <p className="text-sm text-slate-500">{cart.length} items</p>
        </div>
      </div>

      {cart.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="p-10 text-center">
            <ShoppingCart size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500 mb-4">Your cart is empty</p>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => navigate('/nas-portal/catalog')}
            >
              <Package size={18} className="mr-2" />
              Browse Products
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-3">
            {cart.map(item => (
              <Card key={item.product_id} className="bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-800">{item.name}</h3>
                      <p className="text-sm text-slate-500">
                        ₱{item.price?.toLocaleString()} per {item.unit}
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={() => updateCart(item.product_id, item.quantity - 1)}
                        >
                          <Minus size={14} />
                        </Button>
                        <span className="w-12 text-center font-medium">{item.quantity}</span>
                        <Button
                          size="icon"
                          variant="outline"
                          className="h-8 w-8"
                          onClick={() => updateCart(item.product_id, item.quantity + 1)}
                        >
                          <Plus size={14} />
                        </Button>
                      </div>
                      
                      {/* Item Total */}
                      <div className="text-right min-w-[80px]">
                        <p className="font-semibold text-green-600">
                          ₱{(item.price * item.quantity).toLocaleString()}
                        </p>
                      </div>
                      
                      {/* Remove Button */}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => removeItem(item.product_id)}
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            <div className="flex justify-end">
              <Button variant="outline" onClick={clearCart} className="text-red-600">
                <Trash2 size={16} className="mr-2" />
                Clear Cart
              </Button>
            </div>
          </div>
          
          {/* Order Summary */}
          <div className="space-y-4">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items Summary */}
                <div className="space-y-2 pb-3 border-b">
                  {cart.map(item => (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span className="text-slate-600">{item.name} × {item.quantity}</span>
                      <span>₱{(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                
                {/* Total */}
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-green-600">₱{totalAmount.toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
            
            {/* Delivery Info */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin size={18} />
                  Delivery Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Delivery Address *</Label>
                  <Textarea
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder="Enter complete delivery address"
                    rows={3}
                    data-testid="delivery-address-input"
                  />
                </div>
                
                <div>
                  <Label>Payment Method</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger data-testid="payment-method-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cod">Cash on Delivery (COD)</SelectItem>
                      <SelectItem value="pay_later">Pay Later (Credit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Order Notes (Optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Special instructions, preferred delivery time..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
            
            {/* Submit Button */}
            <Button
              className="w-full bg-green-600 hover:bg-green-700 h-12 text-lg"
              onClick={handleSubmitOrder}
              disabled={isSubmitting || cart.length === 0}
              data-testid="place-order-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="mr-2 animate-spin" />
                  Placing Order...
                </>
              ) : (
                <>
                  <CheckCircle size={20} className="mr-2" />
                  Place Order
                </>
              )}
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              Your order will be reviewed by our team before processing.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerCart;
